import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "fileInput",
    "uploadButton",
    "progress",
    "progressBar",
    "progressText",
    "status",
  ];

  connect() {
    // In Stimulus 1.x, data-skill-sheet-upload-skill-id becomes dataset.skillSheetUploadSkillId
    this.skillId = this.element.dataset.skillSheetUploadSkillId;
    console.log("Skill sheet upload controller connected", this.skillId);
  }

  fileSelected() {
    const file = this.fileInputTarget.files[0];
    if (file) {
      // Enable upload button
      this.uploadButtonTarget.disabled = false;

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.showError("File is too large. Maximum size is 10MB.");
        this.uploadButtonTarget.disabled = true;
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (
        !allowedTypes.includes(file.type) &&
        !file.name.match(/\.(pdf|doc|docx)$/i)
      ) {
        this.showError(
          "Invalid file type. Please upload a PDF, DOC, or DOCX file."
        );
        this.uploadButtonTarget.disabled = true;
        return;
      }

      this.clearStatus();
    } else {
      this.uploadButtonTarget.disabled = true;
    }
  }

  async upload() {
    const file = this.fileInputTarget.files[0];
    if (!file) return;

    try {
      // Disable upload button
      this.uploadButtonTarget.disabled = true;

      // Show progress
      this.showProgress("Preparing upload...");

      // Step 1: Get presigned URL from Rails
      const presignResponse = await fetch(
        `/skills/${this.skillId}/presign_skill_sheet_upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type || "application/octet-stream",
          }),
        }
      );

      if (!presignResponse.ok) {
        const error = await presignResponse.json();
        throw new Error(error.error || "Failed to get upload URL");
      }

      const { url, key, headers } = await presignResponse.json();

      // Step 2: Upload file directly to S3
      this.updateProgress("Uploading to S3...", 33);

      const uploadResponse = await fetch(url, {
        method: "PUT",
        body: file,
        headers: headers,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3");
      }

      // Step 3: Notify Rails of uploaded key
      this.updateProgress("Processing with AI...", 66);

      const submitResponse = await fetch(
        `/skills/${this.skillId}/submit_skill_sheet_key`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            s3_key: key,
            content_type: file.type || "application/octet-stream",
          }),
        }
      );

      if (!submitResponse.ok) {
        const error = await submitResponse.json();
        throw new Error(error.error || "Failed to enqueue processing");
      }

      const result = await submitResponse.json();

      // Step 4: Show success
      this.updateProgress("Extracting description and checklist steps...", 100);

      if (result.queued) {
        this.showSuccess(
          "Upload successful! AI is now extracting the skill description and checklist steps. " +
            "This usually takes 30-60 seconds. Refresh this page in a minute to see the results."
        );
      } else {
        this.showWarning(
          "File uploaded successfully, but processing queue is not configured. " +
            "Contact your administrator to enable AI extraction."
        );
      }

      // Clear file input
      this.fileInputTarget.value = "";
    } catch (error) {
      console.error("Upload error:", error);
      this.showError(`Upload failed: ${error.message}`);
      this.uploadButtonTarget.disabled = false;
    }
  }

  showProgress(text) {
    this.progressTarget.style.display = "block";
    this.progressTextTarget.textContent = text;
    this.statusTarget.innerHTML = "";
  }

  updateProgress(text, percent) {
    this.progressTextTarget.textContent = text;
    if (percent) {
      this.progressBarTarget.style.width = `${percent}%`;
    }
  }

  showSuccess(message) {
    this.progressTarget.style.display = "none";
    this.statusTarget.innerHTML = `
      <div class="alert alert-success">
        <strong><i class="glyphicon glyphicon-ok"></i> Success!</strong> ${message}
      </div>
    `;
  }

  showWarning(message) {
    this.progressTarget.style.display = "none";
    this.statusTarget.innerHTML = `
      <div class="alert alert-warning">
        <strong><i class="glyphicon glyphicon-warning-sign"></i> Warning:</strong> ${message}
      </div>
    `;
  }

  showError(message) {
    this.progressTarget.style.display = "none";
    this.statusTarget.innerHTML = `
      <div class="alert alert-danger">
        <strong><i class="glyphicon glyphicon-exclamation-sign"></i> Error:</strong> ${message}
      </div>
    `;
  }

  clearStatus() {
    this.statusTarget.innerHTML = "";
    this.progressTarget.style.display = "none";
  }
}
